import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { dmUnlockApi } from '../../../api/dmUnlock';
import { PersonUnlockResponse, Place } from '../../../types/results.types';
import { useDMUnlockPolling } from '../../../hooks/useDMUnlockPolling';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { findablePeopleIds } from '../../../utils/rowActions';

interface DMUnlockButtonProps {
  personId: number;
  personName: string;
  searchId?: number;
  onSuccess?: (response: PersonUnlockResponse) => void;
  onBalanceUpdate?: () => void;
  place?: Place;
  hasDiscoveredEmail?: boolean;
  compact?: boolean;
  onUpgradeClick?: () => void;
}

const DMUnlockButton: React.FC<DMUnlockButtonProps> = ({
  personId,
  personName,
  searchId,
  onSuccess,
  onBalanceUpdate,
  place,
  hasDiscoveredEmail = false,
  compact = false,
  onUpgradeClick,
}) => {
  const [result, setResult] = useState<PersonUnlockResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStartingUnlock, setIsStartingUnlock] = useState(false);

  // True if any active batch (of any kind) covers this personId
  const isBatchPollingForMe = useAppSelector((state) => {
    const batches = Object.values(state.dmUnlock.activeBatches);
    if (batches.length === 0) return false;
    return batches.some(
      (b) => b.personIds.length === 0 || b.personIds.includes(personId),
    );
  });

  const { startPolling, isPolling } = useDMUnlockPolling({
    searchId,
    personIds: [personId],
    kind: 'row',
    onComplete: async () => {
      if (!personId) return;
      try {
        const finalResult = await dmUnlockApi.unlockPerson(
          personId,
          searchId ? { search_id: searchId } : undefined,
        );
        setResult(finalResult);
        if (finalResult.success && onSuccess) onSuccess(finalResult);
      } catch {
        // fetchResults from polling hook will refresh parent
      }
      if (onBalanceUpdate) onBalanceUpdate();
    },
    onError: () => {
      setError('Failed to discover email. Please try again.');
    },
  });

  const isLoading = isStartingUnlock || isPolling || (isBatchPollingForMe && result === null);

  if (place && !findablePeopleIds(place).includes(personId) && !hasDiscoveredEmail) return null;

  const handleUnlock = async () => {
    setError(null);
    setIsStartingUnlock(true);

    try {
      const response = await dmUnlockApi.unlockPerson(
        personId,
        searchId ? { search_id: searchId } : undefined,
      );

      if (response.queued && response.batch_id) {
        startPolling(response.batch_id);
      } else if (!response.queued) {
        setResult(response);
        if (response.success) {
          if (onSuccess) onSuccess(response);
          if (onBalanceUpdate) onBalanceUpdate();
        }
      }
    } catch (err: any) {
      if (err.response?.status === 402 && onUpgradeClick) {
        onUpgradeClick();
        return;
      }
      setError(err.response?.data?.detail || 'Failed to complete enrichment. Please try again.');
    } finally {
      setIsStartingUnlock(false);
    }
  };

  if (isLoading) {
    if (compact) {
      return (
        <button
          disabled
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-400 text-xs font-semibold cursor-not-allowed"
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Finding...
        </button>
      );
    }
    return (
      <button
        disabled
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-gray-300 text-gray-400 bg-white text-sm font-semibold cursor-not-allowed"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        {hasDiscoveredEmail ? 'Verifying...' : 'Finding...'}
      </button>
    );
  }

  if (result?.success && result.email) {
    if (compact) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Found
        </span>
      );
    }
    return (
      <div className="space-y-2">
        <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 rounded-lg p-3">
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-900 break-all">{result.email}</p>
            <div className="flex items-center gap-2 mt-1">
              {result.verification_status && (
                <span className="text-xs text-green-700">
                  {result.verification_status === 'deliverable' ? 'Verified' : result.verification_status}
                </span>
              )}
              {result.is_catch_all && (
                <span
                  title="This domain accepts any address, so we can't confirm whether this inbox exists."
                  className="text-xs bg-amber-100 text-amber-800 border border-amber-400 px-1.5 py-0.5 rounded font-medium"
                >
                  Catch-all
                </span>
              )}
              {(result.dm_unlocks_charged ?? 0) > 0 && (
                <span className="text-xs text-green-700">
                  ({result.dm_unlocks_charged}{' '}
                  {result.dm_unlocks_charged === 1 ? 'people enrichment' : 'people enrichments'} used)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (result?.success && !result.email) {
    if (compact) {
      return <span className="text-xs text-gray-400 italic">No email found</span>;
    }
    return (
      <div className="flex items-start gap-2.5 bg-gray-50 border border-gray-200 rounded-lg p-3">
        <XCircle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-gray-700">No email found for {personName}</p>
          <p className="text-xs text-gray-500 mt-0.5">No charge applied</p>
        </div>
      </div>
    );
  }

  if (error) {
    if (compact) {
      return (
        <button
          onClick={handleUnlock}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-400 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors"
        >
          Retry
        </button>
      );
    }
    return (
      <div className="space-y-2">
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-3">
          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-900">{error}</p>
          </div>
        </div>
        <button
          onClick={handleUnlock}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[#8A3033] text-[#8A3033] bg-white text-sm font-semibold hover:bg-[#8A3033] hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={handleUnlock}
        disabled={isLoading}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#8A3033] text-[#8A3033] text-xs font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Mail className="w-3.5 h-3.5" />
        Find email
      </button>
    );
  }

  return (
    <button
      onClick={handleUnlock}
      disabled={isLoading}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[#8A3033] text-[#8A3033] bg-white text-sm font-semibold hover:bg-[#8A3033] hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Mail className="w-4 h-4" />
      {hasDiscoveredEmail ? 'Verify Email' : 'Find Email'}
    </button>
  );
};

export default DMUnlockButton;
